(() => {
  'use strict';

  const MODULE_KEY = '__totokaNewArrivalsLog';
  const ROOT_SELECTOR = '[data-tna-root]';

  if (window[MODULE_KEY]) {
    window[MODULE_KEY].mount(document);
    return;
  }

  const instances = new WeakMap();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class TotokaNewArrivalsLog {
    constructor(root) {
      this.root = root;
      this.reveals = [...root.querySelectorAll('[data-tna-reveal]')];
      this.entries = [...root.querySelectorAll('[data-tna-entry]')];
      this.media = [...root.querySelectorAll('[data-tna-product-media]')];
      this.timeline = root.querySelector('[data-tna-timeline]');
      this.observer = null;
      this.frame = null;
      this.destroyed = false;
      this.onViewportChange = this.onViewportChange.bind(this);

      this.init();
    }

    init() {
      if (reduceMotion.matches) {
        this.showAll();
        this.root.style.setProperty('--tna-timeline-progress', '1');
        return;
      }

      this.root.classList.add('is-motion-ready');

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (observations) => {
            observations.forEach((observation) => {
              if (!observation.isIntersecting) return;
              observation.target.classList.add('is-visible');
              this.observer?.unobserve(observation.target);
            });
          },
          { rootMargin: '0px 0px -7% 0px', threshold: 0.1 }
        );

        this.reveals.forEach((element) => this.observer.observe(element));
      } else {
        this.showAll();
      }

      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });
      this.onViewportChange();
    }

    showAll() {
      this.reveals.forEach((element) => element.classList.add('is-visible'));
    }

    onViewportChange() {
      if (this.frame || this.destroyed) return;

      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.updateTimeline();
        this.updateCurrentEntry();
        this.updateParallax();
      });
    }

    updateTimeline() {
      if (!this.timeline) return;

      const rect = this.timeline.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const start = viewportHeight * 0.72;
      const travel = Math.max(rect.height, 1);
      const progress = clamp((start - rect.top) / travel);

      this.root.style.setProperty('--tna-timeline-progress', progress.toFixed(4));
    }

    updateCurrentEntry() {
      if (!this.entries.length) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const activePoint = viewportHeight * 0.5;
      let currentEntry = null;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.entries.forEach((entry) => {
        const rect = entry.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const distance = Math.abs(center - activePoint);
        const isNearViewport = rect.bottom > viewportHeight * 0.16 && rect.top < viewportHeight * 0.84;

        if (isNearViewport && distance < closestDistance) {
          closestDistance = distance;
          currentEntry = entry;
        }
      });

      this.entries.forEach((entry) => entry.classList.toggle('is-current', entry === currentEntry));
    }

    updateParallax() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      if (window.innerWidth < 750) {
        this.media.forEach((element) => element.style.setProperty('--tna-parallax', '0px'));
        return;
      }

      const intensity = this.root.dataset.animationIntensity || 'standard';
      const multiplier = intensity === 'subtle' ? 0.65 : intensity === 'strong' ? 1.25 : 1;

      this.media.forEach((element) => {
        const mediaRect = element.getBoundingClientRect();
        const mediaCenter = mediaRect.top + mediaRect.height / 2;
        const normalized = clamp((mediaCenter - viewportHeight / 2) / viewportHeight, -1, 1);
        const entry = element.closest('[data-tna-entry]');
        const role = entry?.dataset.tnaRole;
        const amplitude = role === 'latest' ? 8 : role === 'recent' ? 12 : 15;

        element.style.setProperty('--tna-parallax', `${(-normalized * amplitude * multiplier).toFixed(2)}px`);
      });
    }

    revealBlock(block) {
      const entry = block?.closest?.('[data-tna-entry]');
      if (!entry) return;
      entry.classList.add('is-visible', 'is-current');
    }

    destroy() {
      this.destroyed = true;
      this.observer?.disconnect();
      this.observer = null;

      if (this.frame) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      this.root.classList.remove('is-motion-ready');
      this.root.style.removeProperty('--tna-timeline-progress');
      this.entries.forEach((entry) => entry.classList.remove('is-current'));
      this.media.forEach((element) => element.style.removeProperty('--tna-parallax'));
    }
  }

  const mount = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll?.(ROOT_SELECTOR) || []];

    roots.forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new TotokaNewArrivalsLog(root));
    });
  };

  const unmount = (scope) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll?.(ROOT_SELECTOR) || []];

    roots.forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  };

  const start = () => mount(document);

  window[MODULE_KEY] = { mount, unmount };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    const selectedBlock = blockId
      ? document.querySelector(`${ROOT_SELECTOR} [data-block-id="${CSS.escape(blockId)}"]`)
      : event.target.closest?.('[data-tna-entry]');
    const root = selectedBlock?.closest?.(ROOT_SELECTOR) || event.target.closest?.(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.revealBlock(selectedBlock || event.target);
  });
})();
